import axios from 'axios'
import { useState } from 'react'
import { replace, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { LoginAPI } from '../../AllAPI/AdminAPI'

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --gold: #ffc107;
    --gold-deep: #e6a800;
    --gold-light: #fff3cd;
    --gold-pale: #fffbec;
    --dark: #1a1200;
    --dark-2: #2b1f00;
    --dark-3: #3d2e00;
    --navy: #141428;
    --navy-2: #0e1e1a;
    --text: #1a1200;
    --text-muted: #7a6000;
    --white: #ffffff;
    --shadow: 0 8px 40px rgba(255,193,7,0.18);
    --radius: 18px;
  }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--gold-pale);
    min-height: 100vh;
  }

  /* ─── PAGE WRAPPER ─── */
  .page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--gold-pale);
    position: relative;
    overflow: hidden;
  }

  /* Decorative blobs */
  .page::before {
    content: '';
    position: fixed;
    top: -120px; right: -120px;
    width: 480px; height: 480px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,193,7,0.15) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
  .page::after {
    content: '';
    position: fixed;
    bottom: -80px; left: -80px;
    width: 380px; height: 380px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,193,7,0.10) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  /* ─── HEADER ─── */
  .djb-header {
    position: relative;
    z-index: 10;
    background: linear-gradient(115deg, #1a1200 0%, #2b1f00 60%, #3d2e00 100%);
    padding: 16px 32px;
    display: flex;
    align-items: center;
    gap: 18px;
    box-shadow: 0 4px 28px rgba(255,193,7,0.22);
    border-bottom: 2.5px solid var(--gold);
  }

  .djb-logo-ring {
    width: 68px; height: 68px;
    border-radius: 50%;
    border: 2.5px solid var(--gold);
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(145deg, #2b1f00, #3d2e00);
    box-shadow: 0 0 0 5px rgba(255,193,7,0.12), 0 4px 18px rgba(0,0,0,0.5);
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
  }
  .djb-logo-ring::after {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,193,7,0.22), transparent);
    animation: shimmer 3s infinite;
  }
  @keyframes shimmer {
    0%   { left: -100%; }
    60%, 100% { left: 160%; }
  }
  .djb-logo-icon { font-size: 30px; }

  .djb-header-text h1 {
    font-family: 'Playfair Display', serif;
    font-size: 1.55rem;
    font-weight: 900;
    color: var(--gold);
    letter-spacing: 0.4px;
    line-height: 1.15;
  }
  .djb-header-text p {
    font-size: 0.78rem;
    color: rgba(255,193,7,0.55);
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 3px;
    font-weight: 500;
  }
  .djb-header-badge {
    margin-left: auto;
    background: rgba(255,193,7,0.12);
    border: 1px solid rgba(255,193,7,0.28);
    color: var(--gold);
    font-size: 0.7rem;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: 50px;
    font-weight: 600;
    white-space: nowrap;
  }

  /* ─── MAIN ─── */
  .djb-main {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 44px 20px;
    position: relative;
    z-index: 2;
  }

  /* ─── CARD ─── */
  .djb-card {
    background: var(--white);
    border-radius: var(--radius);
    box-shadow: var(--shadow), 0 2px 0 var(--gold);
    padding: 42px 38px 34px;
    width: 100%;
    max-width: 430px;
    border: 1.5px solid rgba(255,193,7,0.18);
    position: relative;
    animation: cardIn 0.55s cubic-bezier(.22,1,.36,1) both;
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(28px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* animated top bar */
  .djb-card-accent {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    border-radius: var(--radius) var(--radius) 0 0;
    background: linear-gradient(90deg, var(--gold-deep), var(--gold), #ffe066, var(--gold));
    background-size: 200%;
    animation: gradMove 3s linear infinite;
  }
  @keyframes gradMove {
    0%   { background-position: 0%; }
    100% { background-position: 200%; }
  }

  .djb-card-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--dark);
    text-align: center;
    margin-bottom: 4px;
  }
  .djb-card-sub {
    text-align: center;
    font-size: 0.82rem;
    color: var(--text-muted);
    margin-bottom: 28px;
    letter-spacing: 0.4px;
  }

  /* ─── FORM ─── */
  .djb-form-group { margin-bottom: 16px; }

  .djb-label {
    display: block;
    font-size: 0.76rem;
    font-weight: 600;
    color: var(--dark);
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .djb-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .djb-input-icon {
    position: absolute;
    left: 13px;
    font-size: 0.95rem;
    pointer-events: none;
    color: var(--text-muted);
  }

  .djb-input {
    width: 100%;
    padding: 11px 13px 11px 38px;
    border: 1.5px solid #e9d88a;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.93rem;
    color: var(--dark);
    background: var(--gold-pale);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  }
  .djb-input:focus {
    border-color: var(--gold);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(255,193,7,0.13);
  }

  .djb-toggle-pass {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.95rem;
    color: var(--text-muted);
    padding: 0;
    transition: color 0.15s;
  }
  .djb-toggle-pass:hover { color: var(--gold-deep); }

  /* row: register + forgot */
  .djb-row-opts {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 22px;
    margin-top: 4px;
    flex-wrap: wrap;
    gap: 6px;
  }
  .djb-link {
    font-size: 0.81rem;
    color: var(--gold-deep);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.15s;
  }
  .djb-link:hover { color: var(--dark); }

  /* submit button */
  .djb-btn-login {
    width: 100%;
    padding: 13px;
    background: linear-gradient(135deg, var(--gold-deep) 0%, var(--gold) 60%, #ffe066 100%);
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.97rem;
    font-weight: 700;
    color: var(--dark);
    cursor: pointer;
    letter-spacing: 0.4px;
    box-shadow: 0 4px 18px rgba(255,193,7,0.32);
    transition: transform 0.15s, box-shadow 0.15s, filter 0.15s;
    position: relative;
    overflow: hidden;
  }
  .djb-btn-login::after {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent);
    transition: left 0.4s;
  }
  .djb-btn-login:hover::after { left: 160%; }
  .djb-btn-login:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 26px rgba(255,193,7,0.42);
    filter: brightness(1.04);
  }
  .djb-btn-login:active { transform: translateY(0); }
  .djb-btn-login:disabled { opacity: 0.72; cursor: not-allowed; transform: none; }

  /* divider */
  .djb-divider {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 18px 0 0;
  }
  .djb-divider-line { flex: 1; height: 1px; background: #f0dfa0; }
  .djb-divider-text { font-size: 0.73rem; color: #b8a040; font-weight: 500; letter-spacing: 1px; }

  /* ─── FOOTER ─── */
  .djb-footer {
    position: relative;
    z-index: 10;
    background: linear-gradient(115deg, #141428, #0e1e1a);
    color: rgba(255,255,255,0.7);
    padding: 34px 40px 18px;
    border-top: 2.5px solid var(--gold);
  }

  .djb-footer-grid {
    display: grid;
    grid-template-columns: 1.5fr 1fr 1.5fr;
    gap: 30px;
    max-width: 960px;
    margin: 0 auto 24px;
  }

  .djb-footer-col h4 {
    font-family: 'Playfair Display', serif;
    font-size: 0.92rem;
    color: var(--gold);
    margin-bottom: 11px;
    letter-spacing: 0.4px;
  }
  .djb-footer-col p,
  .djb-footer-col a {
    font-size: 0.80rem;
    line-height: 1.75;
    color: rgba(255,255,255,0.58);
    text-decoration: none;
    display: block;
    transition: color 0.15s;
  }
  .djb-footer-col a:hover { color: var(--gold); }

  .djb-portal-link {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255,193,7,0.09);
    border: 1px solid rgba(255,193,7,0.24);
    color: var(--gold) !important;
    border-radius: 7px;
    padding: 7px 14px;
    font-size: 0.78rem !important;
    font-weight: 600;
    letter-spacing: 0.4px;
    margin-bottom: 8px;
    transition: background 0.2s, border-color 0.2s !important;
    cursor: pointer;
  }
  .djb-portal-link:hover {
    background: rgba(255,193,7,0.18) !important;
    border-color: rgba(255,193,7,0.5) !important;
    color: #fff !important;
  }

  .djb-footer-bottom {
    border-top: 1px solid rgba(255,193,7,0.14);
    padding-top: 14px;
    text-align: center;
    font-size: 0.74rem;
    color: rgba(255,255,255,0.28);
    letter-spacing: 0.4px;
  }
  .djb-footer-bottom span { color: var(--gold); }

  /* ─── MODAL INPUT OVERRIDE ─── */
  .djb-modal-input {
    width: 100%;
    padding: 10px 13px;
    border: 1.5px solid #e9d88a;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.92rem;
    color: var(--dark);
    background: var(--gold-pale);
    outline: none;
    margin-top: 6px;
    margin-bottom: 10px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .djb-modal-input:focus {
    border-color: var(--gold);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(255,193,7,0.12);
  }
  .djb-modal-label {
    font-size: 0.83rem;
    font-weight: 600;
    color: var(--dark);
    letter-spacing: 0.5px;
    display: block;
    margin-top: 8px;
  }
  .djb-modal-btn {
    width: 100%;
    margin-top: 14px;
    padding: 12px;
    background: linear-gradient(135deg, var(--gold-deep) 0%, var(--gold) 60%, #ffe066 100%);
    border: none;
    border-radius: 9px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--dark);
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(255,193,7,0.28);
    transition: transform 0.15s, filter 0.15s;
  }
  .djb-modal-btn:hover { transform: translateY(-1px); filter: brightness(1.05); }
  .djb-modal-btn:disabled { opacity: 0.7; cursor: not-allowed; }

  /* ─── RESPONSIVE ─── */
  @media (max-width: 640px) {
    .djb-header { padding: 14px 16px; gap: 12px; }
    .djb-header-text h1 { font-size: 1.1rem; }
    .djb-header-badge { display: none; }
    .djb-card { padding: 30px 20px 24px; }
    .djb-footer { padding: 26px 16px 14px; }
    .djb-footer-grid { grid-template-columns: 1fr; gap: 18px; }
  }
`

export default function AdminLoginPage({ onLogin }) {
  const [showPass, setShowPass] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleLogin = async (e) => {
    e.preventDefault()

    if (!username.trim() || !password.trim()) {
      toast.error('Please enter both username and password')
      return
    }

    setLoading(true)

    try {
      const res = await axios.post(LoginAPI, {
        username: username,
        password: password,
      })

      const data = res.data
      setLoading(false)

      // if (data.role == '3') {
      //   toast.error('You are not authorized to access Office Login')
      //   return
      // }

      if (res.data == 'REQUIRED') {
        toast.warn('All fields required!')
      } else if (res.data == 'INVALID') {
        toast.warn('Invalid data!')
      } else if (res.data == 'ERROR') {
        toast.warn('ERROR!')
        } else if (res.data !== null) {
          const roleMap = {
            '1': 'admin',
            '2': 'warden',
            '0': 'superintendent',
            '3': 'office'
          }
          const roleName = roleMap[data.role] // convert 1→admin, 2→warden, 0→superintendent , 3->office 
          console.log(res.data)
          sessionStorage.setItem('username', data.username)
          sessionStorage.setItem('branch_id', data.branch_id)
          sessionStorage.setItem('admin_id', data.admin_id)
          sessionStorage.setItem('role', roleName)

          toast.success('Login successful!')
          onLogin(roleName)
          if(roleName==="office"){
            navigate("/office/login-branch-year", {replace: true})
          } else{
            navigate(`/${roleName}/login-branch-year`, { replace: true })
          }
          // navigate(`/${roleName}/login-branch-year`, { replace: true })
        }
    } catch (error) {
      setLoading(false)
      toast.error('Something went wrong. Please try again.')
      console.error('Error:', error)
    }
  }

  return (
    <>
      <style>{style}</style>
      <div className="page">
        {/* ══════════ HEADER ══════════ */}
        <header className="djb-header">
          <div className="djb-logo-ring">
            <span className="djb-logo-icon">🏛️</span>
          </div>
          <div className="djb-header-text">
            <h1>Digambar Jain Boarding, Kolhapur</h1>
            <p>Student Hostel &amp; Accommodation</p>
          </div>
          <span className="djb-header-badge">Est. 1905</span>
        </header>

        {/* ══════════ MAIN ══════════ */}
        <main className="djb-main">
          <div className="djb-card">
            <div className="djb-card-accent" />

            <h2 className="djb-card-title">Admin Login</h2>
            <p className="djb-card-sub">Access your hostel portal account</p>

            <form onSubmit={handleLogin} autoComplete="off">
              {/* Username */}
              <div className="djb-form-group">
                <label className="djb-label" htmlFor="username">
                  Username
                </label>
                <div className="djb-input-wrap">
                  <span className="djb-input-icon">👤</span>
                  <input
                    id="username"
                    className="djb-input"
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
              </div>

              {/* Password */}
              <div className="djb-form-group">
                <label className="djb-label" htmlFor="password">
                  Password
                </label>
                <div className="djb-input-wrap">
                  <span className="djb-input-icon">🔒</span>
                  <input
                    id="password"
                    className="djb-input"
                    type={showPass ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="djb-toggle-pass"
                    onClick={() => setShowPass((v) => !v)}
                    aria-label="Toggle password visibility"
                  >
                    {showPass ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <button
                className="djb-btn-login"
                type="submit"
                disabled={loading}
              >
                {loading ? 'Signing in…' : 'Sign In →'}
              </button>
            </form>

            {/* <div className="djb-divider">
              <div className="djb-divider-line" />
              <span className="djb-divider-text">SECURE PORTAL</span>
              <div className="djb-divider-line" />
            </div> */}
          </div>
        </main>

        {/* ══════════ FOOTER ══════════ */}
        <footer className="djb-footer">
          <div className="djb-footer-grid">
            {/* About */}
            <div className="djb-footer-col">
              <h4>🏛️ Digambar Jain Boarding, Kolhapur</h4>
              <p>
                Providing safe, comfortable &amp; affordable accommodation to
                students since 1905. Your home away from home.
              </p>
            </div>

            {/* Staff Portals */}
            <div className="djb-footer-col">
              <h4>🔐 Staff Portals</h4>
              <a href="#/student/login" className="djb-portal-link">
                ⚙️ Student Login
              </a>
              <a href="#/alumni/login" className="djb-portal-link">
                ⚙️ Alumni Login
              </a>
            </div>

            {/* Contact */}
            {/* 8412841008, 0231 264 4339 */}
            {/* djbkkop@gmail.com */}

            <div className="djb-footer-col">
              <h4>📍 Contact Us</h4>
              <p>📞 +91 8412841008, 0231-2644339</p>
              <a href="mailto:dbjainsabha@gmail.com">✉️ djbkkop@gmail.com</a>
              <p style={{ marginTop: '6px' }}>
                🏠 Near, Dasara Chowk, New Shahupuri,
                <br />
                Kolhapur, Maharashtra 416002
              </p>
            </div>
          </div>

          <div className="djb-footer-bottom">
            © {new Date().getFullYear()}{' '}
            <span>Digambar Jain Boarding, Kolhapur</span>. All rights reserved.
            &nbsp;|&nbsp; Designed by <span>TechnoAarv Solution</span>.
          </div>
        </footer>
      </div>
    </>
  )
}
